// Read-cursor store — Solid signal-map of last-read message id per
// (networkSlug, channel) hydrated from the server.
//
// The cursor is "the row the operator is currently looking at". Server
// is the authority (per CLAUDE.md "Read state is server-owned"); cic
// hydrates from three sources:
//   1. `/me` envelope (`read_cursors: %{slug => %{chan => id}}`) at
//      login via `applyMeEnvelope/1` — cold-load bulk hydration.
//   2. Phoenix Channel join reply (`%{read_cursor: <id_or_nil>}`) per
//      per-channel topic via `applyJoinReply/3` — refresh on every
//      reconnect/rejoin, FORWARD-ONLY since issue 2052 (it used to land
//      the reply unconditionally, which made a resume after a failed POST
//      rewind this device's cursor to the server's stale value).
//   3. `read_cursor_set` typed WS event on the per-channel topic via
//      `applyReadCursorSet/3` — cross-device live sync (device A
//      settles, device B reflects).
//
// Writes go to the server via `setReadCursor(bearer, slug, chan, id)`,
// which POSTs to `/networks/:slug/channels/:chan/read-cursor`. The
// server is last-write-wins; cic sends eagerly without debounce on
// every settle event (selection.ts focus-leave, browser-blur, future
// scroll-settle). `setReadCursor` advances THIS device's signal map
// optimistically (forward-only) before the POST, so the originating
// device reflects its own write in the same synchronous flush rather
// than waiting a server round-trip — the fix for the leave-flicker and
// own-msg-unread bugs (see its inline comment). The POST's
// `read_cursor_set` WS broadcast then re-affirms the id for this device
// and is the ONLY path that lands a peer's set (or a backward move),
// via the arm in subscribe.ts.
//
// Identity-scoped reset: `clearReadCursors()` empties the signal map
// on logout / token rotation, mirroring scrollback.ts / selection.ts /
// members.ts.

import { createEffect, createSignal, on } from "solid-js";
import { token } from "./auth";
import { canonicalChannel } from "./channelKey";
import { moduleRoot } from "./moduleRoot";
import { isVirtualWindowName } from "./windowKinds";

const LEGACY_KEY_PREFIX = "rc:";

// #973 — the ONE fold on the cursor map's KEY boundary, same
// `canonicalChannel` (`asciiFold`, mirror of the server's
// `Grappa.IRC.Identifier.canonical_target/1`) that `channelKey/2` applies.
//
// Pre-fix this interpolated the caller's string verbatim, which forked the
// map for QUERY windows: `query_windows.target_nick` is case-preserving
// server-side and `canonicalQueryNick` deliberately resolves a selection to
// that CASING (it answers "which spelling does this window wear", it is not a
// fold), so every writer on the query path — the `read_cursor_set` WS arm and
// join-reply in subscribe.ts, the settle/leave writes in ScrollbackPane —
// handed us `Mezmerize`. The badge memo (selection.ts `perChannelUnread`)
// reads the cursor under the name it decodes out of a `ChannelKey`, which IS
// folded, so it asked for `mezmerize`, missed, and counted the whole window
// unread. The only writer that landed the folded key was the `/me` cold load
// (the server persists cursors canonicalised), which is why the badge was
// right at page load and then grew monotonically forever.
//
// Folding HERE rather than at each call site is the single-door form the
// key/display/wire split asks for: every entry point (`getReadCursor`,
// `applyMeEnvelope`, `applyJoinReply`, `applyReadCursorSet`, `setReadCursor`'s
// optimistic advance, `renameReadCursorChannel`) inherits it and no future
// caller can get it wrong. It is idempotent, so the already-folded `/me` keys
// are unaffected; it is invisible for channels, whose names arrive canonical
// (raw === folded), which is why only queries ever broke. Display casing is
// untouched — this feeds map KEYS only, never a label. So is the WIRE: the
// POST below still targets the RAW channel, because the server folds at
// ingress and the wire carries the operator's spelling.
const cacheKey = (networkSlug: string, channel: string): string =>
  `${networkSlug} ${canonicalChannel(channel)}`;

const [cursors, setCursors] = moduleRoot(() => createSignal<Record<string, number>>({}));

// The forward-only rule, in one place because two doors owe it: the
// optimistic advance in `setReadCursor` and the join-reply refresh in
// `applyJoinReply`. Both mean the same thing — "adopt this id only if it is
// ahead of what we hold" — and the module's contract above names exactly ONE
// path that may move a cursor backward (`applyReadCursorSet`, the
// authoritative WS echo). Returning `prev` UNCHANGED on a no-op is
// load-bearing, not tidiness: a rebuilt-but-equal object wakes every cursor
// consumer for nothing (the same reason `renameReadCursorChannel` bails early
// on a pure re-casing).
const advanceOnly = (
  prev: Record<string, number>,
  key: string,
  next: number,
): Record<string, number> => {
  const cur = prev[key];
  if (cur !== undefined && next <= cur) return prev;
  return { ...prev, [key]: next };
};

/**
 * Returns the stored read-cursor `last_read_message_id` for
 * `(networkSlug, channel)`, or `null` if none is known. Tracked by
 * Solid — consumers inside reactive contexts re-run when the cursor
 * changes.
 */
export const getReadCursor = (networkSlug: string, channel: string): number | null => {
  const v = cursors()[cacheKey(networkSlug, channel)];
  return v === undefined ? null : v;
};

/**
 * Reactive signal of the entire cursor map keyed by `${slug} ${chan}`.
 * Exposed for memos that derive from the FULL set of cursors (not just
 * one — see `selection.ts`'s `unreadCounts`/`messagesUnread`/
 * `eventsUnread` memos which re-key per channelKey across all hydrated
 * channels). Direct callers should still prefer `getReadCursor/2` for
 * single-key lookups so the signal-map shape stays an implementation
 * detail of this module.
 */
export const readCursors = (): Record<string, number> => cursors();

/**
 * Cursor map key shape — exported so the consumer memo can decode a
 * `${slug} ${chan}` map key back into its parts without re-deriving
 * the separator. Mirrors `cacheKey/2`, so the `channel` it returns is the
 * FOLDED identifier (#973), never a display spelling.
 */
export const decodeCursorKey = (key: string): { slug: string; channel: string } | null => {
  const sep = key.indexOf(" ");
  if (sep === -1) return null;
  return { slug: key.slice(0, sep), channel: key.slice(sep + 1) };
};

/**
 * Bulk-hydrate the signal map from the `/me` envelope's `read_cursors`
 * nested map (`%{slug => %{chan => id}}`). Called once at login by the
 * networks resource. Replaces the entire map — `/me` is the cold-load
 * source of truth and a stale entry from a prior session would mask a
 * cleared cursor.
 */
export const applyMeEnvelope = (envelope: Record<string, Record<string, number>>): void => {
  const next: Record<string, number> = {};
  for (const [slug, perChannel] of Object.entries(envelope)) {
    for (const [chan, id] of Object.entries(perChannel)) {
      if (typeof id === "number" && Number.isFinite(id)) {
        next[cacheKey(slug, chan)] = id;
      }
    }
  }
  setCursors(next);
};

/**
 * Apply the per-channel cursor delivered in a Phoenix Channel join
 * reply (`%{read_cursor: <id_or_nil>}`). `null` is a no-op — the
 * server's "no cursor for this (subject, network, channel)" answer
 * never overwrites a hydrated value. Called from `subscribe.ts` on
 * every successful per-channel join (initial + post-reconnect).
 *
 * **Forward-only** (issue 2052). It used to land the reply's id
 * unconditionally, which made this a second door that moves a cursor
 * BACKWARD — contradicting this module's own contract, which names
 * `applyReadCursorSet` as the only one. The visible cost was a badge that
 * came back after every resume: `setReadCursor` leaves the local cursor
 * optimistically ahead when its POST fails (deliberately — see the
 * no-revert argument at that call site), which is exactly what a stretch
 * offline produces, and the next rejoin then rewound to the server's stale
 * value and re-counted every already-read row as unread.
 *
 * A reply that is AHEAD still lands: that is what the rejoin refresh is
 * FOR (a peer device settled further while this tab was away), and the
 * server's own write is monotonic, so a reply below what we hold cannot be
 * a deliberate regression. `Grappa.ReadCursor`'s moduledoc says so
 * outright — `set/4` clamps backward moves, and deliberate mark-as-unread
 * "has no caller today … when the feature ships it gets its OWN explicit
 * path". When it does, it arrives with a broadcast and lands here through
 * `applyReadCursorSet`, which is where a backward move belongs.
 *
 * Known and accepted: a cross-identity `/me` that seeded a HIGHER cursor
 * for a window can no longer be corrected downward by this door
 * (`networks.ts`'s #818 note). That path is guarded by `identityMoved/1`
 * and the `on(token)` purge; trading it for the resume flicker is the
 * deliberate call, not an oversight.
 */
export const applyJoinReply = (
  networkSlug: string,
  channel: string,
  cursor: number | null,
): void => {
  if (cursor === null) return;
  setCursors((prev) => advanceOnly(prev, cacheKey(networkSlug, channel), cursor));
};

/**
 * Apply a `read_cursor_set` WS event on the per-channel topic. The
 * server emits last-write-wins, including backwards moves; the signal
 * map adopts the new id unconditionally so cross-device settles
 * reflect on every subscribed tab.
 */
export const applyReadCursorSet = (
  networkSlug: string,
  channel: string,
  lastReadMessageId: number,
): void => {
  setCursors((prev) => ({ ...prev, [cacheKey(networkSlug, channel)]: lastReadMessageId }));
};

/**
 * POST `/networks/:slug/channels/:chan/read-cursor` to set the
 * server-side cursor. Eager send — no debounce — because cross-device
 * latency matters and the server absorbs duplicates.
 *
 * Optimistic locally: advances THIS device's signal map forward-only
 * before the POST (see the inline comment), so the originating device
 * reflects its own write synchronously. The server's `read_cursor_set`
 * WS broadcast re-affirms the same id (and is the ONLY path that lands a
 * peer's set, or a backward move) via the arm in subscribe.ts.
 *
 * Returns `void`: the response payload is read only when needed for
 * tests; production code learns its OWN write optimistically and peer
 * writes via the typed WS event.
 *
 * `bearer` is injected at the call site rather than read from `auth`
 * here so this module stays free of the auth ↔ readCursor cycle and
 * tests can drive a deterministic token without `vi.mock`-ing auth.
 */
export const setReadCursor = async (
  bearer: string,
  networkSlug: string,
  channel: string,
  messageId: number,
): Promise<void> => {
  // Virtual/synthetic pseudo-window guard (#160). $home / $admin / $list /
  // mentions("") have NO server-side channel row: this POST 404s ($home/
  // $admin resolve to an unknown network slug) or 400s ($list is not a
  // valid target name). In production nginx feeds those 4xx to fail2ban's
  // http-4xx jail → an idling user is escalated into the `recidive` pf
  // block, cut off web AND IRC. The leak site is ScrollbackPane.onCleanup
  // reading reactive props already advanced to the virtual selection;
  // guarding at THIS boundary — the module that owns the POST, like the
  // messageId contract below — makes every settle/blur/leave/unmount caller
  // inherit it. Skip BOTH the optimistic local advance and the POST: a
  // synthetic window has no cursor to advance. ($server is a REAL
  // scrollback-backed target and is NOT virtual — see isVirtualWindowName.)
  if (isVirtualWindowName(channel)) return;
  // Positive-int boundary guard (issue #44). Service-nick query windows
  // (NickServ/ChanServ/OperServ) settle/blur before a real persisted id
  // exists, so the settle handler can hand us a 0 / NaN / non-integer id.
  // The server's ReadCursorController guards `is_integer(message_id) and
  // message_id > 0` and 400s everything else (31× on prod). There is
  // nothing to mark read without a positive id — skip BOTH the optimistic
  // local advance and the POST. Mirroring the server contract here (the
  // module that owns the POST) means every caller inherits the guard.
  if (!Number.isInteger(messageId) || messageId <= 0) return;
  // Optimistic local advance — forward-only. The signal map is otherwise
  // round-trip-only (applyReadCursorSet lands the id on the server's
  // `read_cursor_set` WS echo), which opens a stale-cursor window between
  // this POST and its broadcast. Reactivity firing in that gap read the
  // OLD cursor and produced two visible bugs:
  //   * sidebar badge flicker when leaving a channel — the focused-window
  //     badge suppression (selection.ts perChannelUnread) drops
  //     synchronously on focus-leave, before the leave-arm's cursor
  //     advance round-tripped, so the badge briefly recomputed non-zero.
  //   * own-sent message rendered above the unread divider after a
  //     switch-away-and-back — the marker re-latch (ScrollbackPane) read
  //     the stale pre-send cursor.
  // Advancing here collapses the write into the same synchronous Solid
  // flush, so the suppression-drop / marker-relatch see the fresh cursor.
  // Forward-only so an in-flight stale POST can't clobber a peer's more-
  // recent advance already landed via the (unconditional, last-write-wins)
  // applyReadCursorSet echo — only that authoritative WS path moves the
  // cursor backward. On SUCCESS the echo for this write re-affirms the
  // same id (no-op set) and the server (last-write-wins) adopts exactly
  // the optimistic value.
  //
  // On a FAILED POST the local cursor is left optimistically ahead of the
  // server — a deliberate trade of the old round-trip-only invariant
  // (local could never diverge) for the no-flicker behavior. It is NOT
  // reverted: a naive revert to the pre-write value would clobber any
  // concurrent forward advance (the very cross-device race the forward-
  // only rule prevents), and a correct compare-and-swap revert is
  // heavyweight machinery for a path that is benign here. cic only ever
  // writes ids it has actually read (visible tail / own send), so the
  // divergence is bounded to already-read rows — a new arrival still has
  // id > cursor and shows unread correctly; nothing NEW is missed. It
  // re-aligns on the next successful forward write, or on /me / join-reply
  // hydration; worst case a relogin right after a failed write re-surfaces
  // already-read rows as unread once. (A 422 from the server's
  // message_belongs? guard is unreachable from here — every posted id is
  // a row cic rendered or sent in this channel.)
  //
  // The in-pane divider reads the FROZEN markerCursorId, never this live
  // signal, so the freeze contract is untouched. The lone exception is
  // the cold-latch effect (ScrollbackPane), which picks up the FIRST
  // non-null cursor while markerCursorId is null — that first value can
  // now be an optimistic one during the narrow cold-load-before-hydration
  // window; pre-existing race shape, see that effect's comment.
  const optimisticKey = cacheKey(networkSlug, channel);
  setCursors((prev) => advanceOnly(prev, optimisticKey, messageId));
  const url = `/networks/${encodeURIComponent(networkSlug)}/channels/${encodeURIComponent(channel)}/read-cursor`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify({ message_id: messageId }),
  });
  if (!res.ok) {
    console.warn(`[readCursor] set failed`, networkSlug, channel, messageId, res.status);
  }
};

/**
 * Empties the signal map. Wired to the `on(token)` cleanup arm below;
 * also called from tests between cases.
 */
export const clearReadCursors = (): void => {
  setCursors({});
};

/**
 * #373 — a query window's peer renamed; move its read cursor from
 * `(networkSlug, oldChannel)` to `(networkSlug, newChannel)`. The server
 * migrates its `read_cursors` row (`ReadCursor.rename_dm_peer/4`) but does
 * NOT broadcast a `read_cursor_set` for it, so this keeps THIS device's
 * cache in step — else the relabeled window derives its unread from a
 * missing cursor (whole history flips unread) until the next settle POST.
 * On a merge (the new key already has a cursor) the existing new cursor
 * wins (mirrors the server keep-new merge). No-op when the old key holds
 * nothing (a member rename with no DM cursor).
 */
export const renameReadCursorChannel = (
  networkSlug: string,
  oldChannel: string,
  newChannel: string,
): void => {
  // The `nick_change` event carries whatever casing the ircd sent on either
  // side, so "same window, nothing to migrate" is a KEY question and folds
  // (#973). A pure re-casing (`Foo` → `foo`) is one identity, not a rename:
  // without the fold it fell through to a self-migration that rebuilt the map
  // into an equal-but-new object and woke every cursor consumer for nothing.
  if (cacheKey(networkSlug, oldChannel) === cacheKey(networkSlug, newChannel)) return;
  setCursors((prev) => {
    const oldKey = cacheKey(networkSlug, oldChannel);
    const oldVal = prev[oldKey];
    if (oldVal === undefined) return prev;
    const newKey = cacheKey(networkSlug, newChannel);
    const { [oldKey]: _drop, ...rest } = prev;
    // Merge: keep the pre-existing new cursor (drop old); else adopt old.
    return newKey in rest ? rest : { ...rest, [newKey]: oldVal };
  });
};

// One-shot purge of the legacy localStorage backend. The pre-flip
// module persisted under `rc:<slug>:<chan>` keys; delete them so a
// freshly-flipped browser doesn't carry stale bytes no code reads.
// Idempotent: a second load finds no `rc:` keys and exits the loop
// without mutating anything. Guarded for non-browser environments
// (vitest jsdom defines `localStorage`; node SSR would not — but cic
// doesn't SSR, the guard is paranoia).
const purgeLegacyKeys = (): void => {
  if (typeof localStorage === "undefined") return;
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith(LEGACY_KEY_PREFIX)) keysToRemove.push(k);
  }
  for (const k of keysToRemove) localStorage.removeItem(k);
};

purgeLegacyKeys();

// Identity-transition cleanup arm. Mirrors the pattern in scrollback.ts,
// selection.ts, members.ts, mentions.ts, compose.ts, subscribe.ts.
// `prev != null` filters both the initial run (prev === undefined) and
// the cold-start login (prev === null) — only logout (tokA→null) and
// rotation (tokA→tokB) trigger the wipe.
moduleRoot(() => {
  createEffect(
    on(token, (t, prev) => {
      if (prev != null && t !== prev) {
        clearReadCursors();
      }
    }),
  );
});
